<template>
  <div :class="recordingActive && 'size-0 overflow-hidden'">
    <div
      ref="recordingPopupRef"
      class="popover-panel absolute right-18 bottom-12 z-1 flex w-72 flex-col gap-3 p-3"
      v-show="!recordingActive"
    >
      <div class="text-group font-medium">{{ $t('recording.Record_your_screen') }}</div>
      <div class="flex flex-col gap-3">
        <ToggleSwitch v-model="recordMicrophone" :label="$t('recording.Record_Microphone')" />
        <ToggleSwitch v-model="recordWebcam" :label="$t('recording.Record_Webcam')" />
      </div>
      <Callout v-if="error" icon="warning" tone="warning">{{ error }}</Callout>
      <SimpleButton
        class="w-full"
        variant="accent"
        @click="startRecordingHandler"
        :disabled="recordingLoading"
        :text="$t('recording.Start_recording')"
      />
    </div>
    <!-- When recording is active we intentionally hide the popup to keep task readable -->
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import {
  initializeStreams, startRecording, cleanUpStreams,
} from '@/utils/screenRecorder';
import ToggleSwitch from '@/components/elements/ToggleSwitch';
import SimpleButton from '@/components/elements/SimpleButton';
import Callout from '@/components/elements/Callout';
import eventBus from '@/eventBus';

const LOCALSTORAGE_SETTINGS_ITEM = 'TRACKLIFY_RECORDING_SETTINGS';


// Refs
const recordingPopupRef = ref(null);
const videoWebcamPreview = ref(null);

// Reactive data
const recordingLoading = ref(false);
const recordMicrophone = ref(true);
const recordWebcam = ref(true);
const recordingActive = ref(false);
const error = ref('');

const store = useStore();
const router = useRouter();

const recordingInitialized = ref(false);

const { t } = useI18n();

// Click outside handler
onClickOutside(recordingPopupRef, (event) => {
  if (!recordingActive.value) {
    if (!event.target.closest('.recording-popup-trigger') && !event.target.closest('.desktop-capturer-selection')) {
      eventBus.$emit('attach-file-to-active', null);
    }
  }
});

// Methods
const persistRecordingPreferences = () => {
  const conf = {
    recordMicrophone: recordMicrophone.value,
    recordWebcam: recordWebcam.value,
  };
  localStorage.setItem(LOCALSTORAGE_SETTINGS_ITEM, JSON.stringify(conf));
};

const initStreams = async () => {
  error.value = '';
  recordingLoading.value = true;
  const { ok, error: streamError } = await initializeStreams({
    recordWebcam: recordWebcam.value,
    recordMicrophone: recordMicrophone.value,
    videoEl: videoWebcamPreview.value,
  });
  if (ok) {
    recordingLoading.value = false;
  }
  if (streamError) {
    error.value = `${t('recording.Cant_start_camera')}: ${streamError}`;
  }
  persistRecordingPreferences();
};

// Request stop via central handler in CommonHeader
const stopRecordingHandler = async () => {
  eventBus.$emit('callStopRecordingHandler');
};

const startRecordingHandler = async () => {
  recordingLoading.value = true;
  const { ok, error: recordError } = await startRecording((file) => {
    // when recording finishes (including external stop)
    store.commit('system/setRecordingActive', false);
    store.commit('system/setRecordingPaused', false);
    eventBus.$emit('attach-file-to-active', file);
  });
  if (recordError) {
    error.value = `${t('recording.cant_start_rec')}: ${recordError}`;
  } else if (ok) {
    // store original task/route to ensure we can restore before stopping
    try {
      window.trackliRec = window.trackliRec || {};
      window.trackliRec.recordingTaskId = store.state.project?.taskUnderEdit?.id;
      window.trackliRec.recordingTaskRoute = router.currentRoute.value.fullPath;
    } catch (e) { /* ignore */ }
    recordingActive.value = true;
    store.commit('system/setRecordingActive', true);
    store.commit('system/setRecordingPaused', false);
  }
  recordingLoading.value = false;
};


// Lifecycle hooks
onMounted(async () => {
  recordingInitialized.value = false;
  const settings = localStorage.getItem(LOCALSTORAGE_SETTINGS_ITEM);
  if (settings) {
    try {
      const conf = JSON.parse(settings);
      recordMicrophone.value = conf.recordMicrophone;
      recordWebcam.value = conf.recordWebcam;
    } catch (e) {
      console.log('LocalStorage fetching error', e);
      // user might always type trash in LocalStorage
    }
  }
  await initStreams();


  // listen for external stop trigger (sidebar button)
  eventBus.$on('screen-recording-stop', async () => {
    if (recordingActive.value) {
      await stopRecordingHandler();
    }
  });

  recordingInitialized.value = true;
});

onUnmounted(() => {
  console.log('❌❌❌ recording popup destroyed' , getCurrentInstance().uid);
  if (!recordingActive.value) { // If still recording, don't cleanup prematurely
    cleanUpStreams();
  }
});

// Watchers
watch(recordMicrophone, () => {
  initStreams();
});

watch(recordWebcam, () => {
  initStreams();
});

defineExpose({
  recordingInitialized,
});
</script>
